import React, { useEffect, useMemo, useState } from 'react';
import { Check, Plus, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../../../../../../../../components/ui/button';
import { Card, CardContent } from '../../../../../../../../components/ui/card';
import { cn } from '../../../../../../../../lib/utils';
import { formatLabel, StatusBadge } from '../../pipeMillReportUtils';
import {
  AdjudicationPanel,
  BendDispositionPanel,
  ChemicalFormulaRow,
  ChemistryResultRow,
  FlatteningDispositionPanel,
  HydrostaticDispositionPanel,
  ItemActionButtons,
  KeyValueRows,
  MiniStatusDot,
  PipeLocationDiagram,
  ReportMetadataRail,
  ResultRow,
  SectionHead,
  StatCard,
  TensileResultRow,
} from './shared/components';
import {
  AUT_DETAIL,
  BEND_DETAIL,
  CHEMICAL_DETAIL,
  CVN_DETAIL,
  DIMENSIONAL_DETAIL,
  HARDNESS_DETAIL,
  HYDROSTATIC_DETAIL,
  STANDARD_REPORT_DETAILS,
} from './shared/sampleData';
import {
  average,
  formatDisplayDate,
  getStandardReportSummaryStats,
  standardLimitText,
  standardScaleMax,
  standardScaleMin,
  statusNoteClassName,
} from './shared/format';
import {
  createSampleMeasurement,
  isSampleDataDeleted,
} from './shared/measurements';
import {
  getProjectLimitField,
  getProjectLimitMax,
  getProjectLimitMin,
  getProjectLimitRows,
  getProjectLimitText,
} from './shared/limits';
import type {
  ChemicalElementDisplay,
  FullReportType,
  ProjectLimitFieldOption,
  ReportDefinition,
  ReportMeasurementItem,
  ReportMetadataEntry,
  ReportSummary,
  StandardCheck,
} from './shared/types';
import type { BaseReportDetailProps } from './shared/reportProps';

export const StandardReportDetail: React.FC<{ reportType: FullReportType; heatNumber: string }> = ({ reportType, heatNumber }) => {
  const detail = STANDARD_REPORT_DETAILS[reportType];
  const [activeCheck, setActiveCheck] = useState(detail?.checks[0]?.key || '');

  if (!detail) return null;

  const check = detail.checks.find((item) => item.key === activeCheck) || detail.checks[0];
  const passCount = detail.checks.filter((item) => item.status === 'PASS').length;
  const issueCount = detail.checks.length - passCount;
  const summaryStats = getStandardReportSummaryStats(reportType, detail, passCount, issueCount);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px]">
      <main className="space-y-5 p-6">
        {(detail.status === 'INCOMPLETE' || detail.status === 'PENDING_REVIEW') && (
          <div className={cn(
            'rounded-lg border p-3 text-[13px]',
            detail.status === 'INCOMPLETE'
              ? 'border-[#E2E5EA] bg-page-bg text-heritage-grey'
              : 'border-[#E7D28A] bg-[rgba(230,161,0,0.10)] text-[#8A6500]'
          )}>
            <span className="font-semibold text-blueprint-blue">{detail.title} is not fully cleared.</span>
            <span className="ml-2">{detail.summary}</span>
          </div>
        )}

        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          {summaryStats.map((stat) => (
            <StatCard key={stat.label} label={stat.label} value={stat.value} sub={stat.sub} status={detail.status === 'PASS' ? 'PASS' : detail.status} />
          ))}
          <StatCard label="Source" value={formatLabel(detail.source)} sub="record" />
        </div>

        <Card className="border-[#E2E5EA]">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Check className={cn('mt-0.5 h-4 w-4 shrink-0', detail.status === 'PASS' ? 'text-galvanizing-green' : 'text-heritage-grey')} />
              <div>
                <h3 className="font-heading text-[14px] font-semibold text-blueprint-blue">Report Summary</h3>
                <p className="mt-1 text-[13px] text-text-grey">{detail.summary}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h3 className="font-heading text-[16px] font-semibold text-blueprint-blue">Acceptance Checks</h3>
            <div className="mt-1 text-[12px] text-heritage-grey">Configured report checks and measured values</div>
          </div>
          <div className="flex flex-wrap gap-2">
            {detail.checks.slice(0, 5).map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => setActiveCheck(item.key)}
                className={cn(
                  'rounded-lg border px-3 py-2 text-[12px] font-semibold',
                  activeCheck === item.key ? 'border-hero-red-20 bg-hero-red-10 text-readable-red' : 'border-[#E2E5EA] bg-white text-blueprint-blue'
                )}
              >
                {item.key}
              </button>
            ))}
          </div>
        </div>

        <Card className="overflow-hidden border-[#E2E5EA]">
          <CardContent className="p-0">
            <SectionHead title={check.key} subtitle={check.label} status={check.status} />
            <TensileResultRow
              label={check.label}
              formula={standardLimitText(check)}
              value={check.value}
              unit={check.unit}
              min={standardScaleMin(check)}
              max={standardScaleMax(check)}
              lo={check.min}
              hi={check.max}
              status={check.status}
            />
          </CardContent>
        </Card>

        <Card className="overflow-hidden border-[#E2E5EA]">
          <CardContent className="p-0">
            <SectionHead title="All Checks" />
            <table className="w-full text-left">
              <thead className="border-b border-[#E2E5EA] bg-page-bg text-[10px] font-bold uppercase tracking-wider text-heritage-grey">
                <tr>
                  <th className="px-4 py-3">Code</th>
                  <th className="px-4 py-3">Check</th>
                  <th className="px-4 py-3 text-right">Measured</th>
                  <th className="px-4 py-3 text-right">Limit</th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#EEF0F3] text-[12px] text-text-grey">
                {detail.checks.map((item) => (
                  <tr key={item.key} onClick={() => setActiveCheck(item.key)} className={cn('cursor-pointer', item.key === activeCheck && 'bg-[#E7F3FF]')}>
                    <td className="px-4 py-3 font-mono font-semibold">{item.key}</td>
                    <td className="px-4 py-3">{item.label}</td>
                    <td className="px-4 py-3 text-right font-mono">{item.value} {item.unit}</td>
                    <td className="px-4 py-3 text-right font-mono">{standardLimitText(item)}</td>
                    <td className="px-4 py-3"><StatusBadge status={item.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <Card className="overflow-hidden border-[#E2E5EA]">
          <CardContent className="p-0">
            <SectionHead title="Observations" />
            <table className="w-full text-left">
              <thead className="border-b border-[#E2E5EA] bg-page-bg text-[10px] font-bold uppercase tracking-wider text-heritage-grey">
                <tr>
                  <th className="px-4 py-3">ID</th>
                  <th className="px-4 py-3">Area</th>
                  <th className="px-4 py-3">Finding</th>
                  <th className="px-4 py-3">Disposition</th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#EEF0F3] text-[12px] text-text-grey">
                {detail.observations.map((item) => (
                  <tr key={item.id}>
                    <td className="px-4 py-3 font-mono font-semibold">{item.id}</td>
                    <td className="px-4 py-3">{item.area}</td>
                    <td className="px-4 py-3">{item.finding}</td>
                    <td className="px-4 py-3">{item.disposition}</td>
                    <td className="px-4 py-3"><StatusBadge status={item.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </main>

      <ReportMetadataRail
        rows={[
          ['Report ID', detail.reportId, true],
          ['Performed at', formatDisplayDate(detail.performedAt), true],
          ['Heat', heatNumber, true],
          ['Station', detail.station],
          ['Owner', detail.owner],
          ['Specification', detail.spec],
          ['Source file', detail.sourceFile, true],
        ]}
        extraTitle="Limits In Effect"
        extraRows={[
          ...detail.limits.map(([key, value]) => [key, value, true] as [string, React.ReactNode, boolean?]),
          ['Source priority', 'project_limits -> standard_limits'],
        ]}
      >
        <AdjudicationPanel />
      </ReportMetadataRail>
    </div>
  );
};